import { Controller } from "@hotwired/stimulus";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { addMapBackground } from "../utils/map_background";

// Places a circuit stop by a click on the map (GO4SFLEET, 20/10/2026): the
// click writes the latitude and longitude fields, the marker can be dragged
// to adjust, and the other stops of the circuit show in grey as landmarks.
// No address search: no paid geocoding service.
export default class extends Controller {
  static targets = ["map", "latitude", "longitude", "others"];

  static values = {
    // Empty: the OpenFreeMap style (utils/map_background.js).
    styleUrl: String,
    tileUrl: String,
    attribution: String,
    center: { type: Array, default: [3.848, 11.502] },
  };

  connect() {
    this.map = L.map(this.mapTarget, { scrollWheelZoom: true }).setView(this.centerValue, 13);
    addMapBackground(this.map, { styleUrl: this.styleUrlValue, tileUrl: this.tileUrlValue, attribution: this.attributionValue });

    const others = this.hasOthersTarget ? JSON.parse(this.othersTarget.textContent || "[]") : [];
    const points = [];
    others.forEach((stop) => {
      if (stop.latitude == null || stop.longitude == null) return;
      L.circleMarker([stop.latitude, stop.longitude], { radius: 6, color: "#94a3b8", fillColor: "#fff", fillOpacity: 1, weight: 2 })
        .bindTooltip(this.escape(`${stop.stop_order}. ${stop.name}`))
        .addTo(this.map);
      points.push([stop.latitude, stop.longitude]);
    });

    const lat = parseFloat(this.latitudeTarget.value);
    const lng = parseFloat(this.longitudeTarget.value);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      this.place([lat, lng]);
      this.map.setView([lat, lng], 16);
    } else if (points.length) {
      this.map.fitBounds(L.latLngBounds(points).pad(0.3), { maxZoom: 16 });
    }

    this.map.on("click", (event) => this.place([event.latlng.lat, event.latlng.lng]));
    // A map drawn while its tab or card was hidden needs its size again.
    setTimeout(() => this.map && this.map.invalidateSize(), 200);
  }

  disconnect() {
    if (this.map) this.map.remove();
    this.map = null;
  }

  place(latlng) {
    if (this.marker) {
      this.marker.setLatLng(latlng);
    } else {
      this.marker = L.marker(latlng, {
        draggable: true,
        icon: L.divIcon({
          className: "",
          html: '<div style="width:22px;height:22px;border-radius:9999px;background:#1d4ed8;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);"></div>',
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        }),
      }).addTo(this.map);
      this.marker.on("dragend", () => this.write(this.marker.getLatLng()));
    }
    this.write(L.latLng(latlng));
  }

  clear() {
    if (this.marker) this.map.removeLayer(this.marker);
    this.marker = null;
    this.latitudeTarget.value = "";
    this.longitudeTarget.value = "";
  }

  // Leaflet puts a tooltip string in innerHTML: a stop name is text.
  escape(text) {
    const div = document.createElement("div");
    div.textContent = text == null ? "" : String(text);
    return div.innerHTML;
  }

  write(latlng) {
    this.latitudeTarget.value = latlng.lat.toFixed(7);
    this.longitudeTarget.value = latlng.lng.toFixed(7);
  }
}
